import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef } from 'react';
import { primary, neutral } from '@/theme';

interface CommuneMapProps {
  geojson: GeoJSON.FeatureCollection | null;
  loading?: boolean;
}

function GeoJsonLayer({ geojson }: { geojson: GeoJSON.FeatureCollection }) {
  const map = useMap();
  const layerRef = useRef<google.maps.Data | null>(null);

  useEffect(() => {
    if (!map) return;

    if (layerRef.current) {
      layerRef.current.setMap(null);
    }

    const data = new google.maps.Data({ map });
    layerRef.current = data;

    data.addGeoJson(geojson);

    data.setStyle((feature) => {
      const area = feature.getProperty('areaKm2') as number;
      const opacity = area > 100 ? 0.35 : area > 20 ? 0.25 : 0.2;
      return {
        fillColor: primary[400],
        fillOpacity: opacity,
        strokeColor: primary[600],
        strokeWeight: 2,
        clickable: true,
      };
    });

    const bounds = new google.maps.LatLngBounds();
    data.forEach((feature) => {
      const geom = feature.getGeometry();
      if (!geom) return;
      geom.forEachLatLng((latlng) => bounds.extend(latlng));
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 48);
    }

    const infoWindow = new google.maps.InfoWindow();
    data.addListener('click', (event: google.maps.Data.MouseEvent) => {
      const name = event.feature.getProperty('name') as string;
      const code = event.feature.getProperty('code') as string;
      const area = event.feature.getProperty('areaKm2') as number;
      infoWindow.setContent(
        `<div style="font-family:inherit;font-size:13px;line-height:1.5">` +
          `<strong>${name}</strong><br/>` +
          `Code INSEE : ${code}<br/>` +
          `Superficie : ${area} km²` +
          `</div>`,
      );
      infoWindow.setPosition(event.latLng);
      infoWindow.open(map);
    });

    return () => {
      infoWindow.close();
      data.setMap(null);
      layerRef.current = null;
    };
  }, [map, geojson]);

  return null;
}

export function CommuneMap({ geojson, loading }: CommuneMapProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

  if (!apiKey) {
    return (
      <div
        style={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: neutral[50],
          borderRadius: 8,
          color: neutral[500],
          fontSize: 13,
        }}
      >
        Clé API Google Maps manquante (VITE_GOOGLE_MAPS_API_KEY)
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <APIProvider apiKey={apiKey}>
        <Map
          defaultCenter={{ lat: 46.227638, lng: 2.213749 }}
          defaultZoom={5}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapId="wisecure-communes"
          style={{ height: 400, width: '100%', borderRadius: 8 }}
        >
          {geojson && <GeoJsonLayer geojson={geojson} />}
        </Map>
      </APIProvider>
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.7)',
            borderRadius: 8,
          }}
        >
          Chargement de la carte…
        </div>
      )}
    </div>
  );
}
